import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import LegalLayout from "../components/legal/LegalLayout";
import { CompanyIcon, EmailIcon, LicenseIcon, PhoneIcon } from "../assets/icons/icons";
import clearBadgeMarkWhite from "../assets/clearbadge-icon-white.png";

const API_BASE = process.env.REACT_APP_API_URL || "";

const VERIFICATION_TOOLTIP_COPY =
  "Verification confirms identity and license status through ClearBadge. It does not imply endorsement, quality, or suitability.";
const UNVERIFIED_TOOLTIP_COPY =
  "Not Verified means this professional has not completed ClearBadge's verification process. It does not imply lack of licensure, quality, or suitability.";

const CLAIM_PAGE_UNCLAIMED_COPY =
  "ClearBadge creates public profiles based on search activity and publicly available licensing information so consumers can verify credentials. Claiming your profile allows you to confirm accuracy and manage what appears publicly.";

export default function ClaimPage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null);
  const [openTooltipFor, setOpenTooltipFor] = useState(null);

  const isTempPlaceholderId = (value) => String(value || "").startsWith("temp-");

  useEffect(() => {
    const handleDocClick = (event) => {
      const target = event?.target;
      if (!target?.closest?.(".info-tooltip-wrap")) {
        setOpenTooltipFor(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event?.key === "Escape") setOpenTooltipFor(null);
    };

    document.addEventListener("click", handleDocClick);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleDocClick);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const renderVerificationPill = (isVerified, tooltipKey) => {
    const pillKey = `pill-${tooltipKey}`;
    return (
      <span className="info-tooltip-wrap pill-tooltip-wrap">
        <button
          type="button"
          className={`pill-tooltip-btn ${isVerified ? "license-verify-badge vettify-pill-badge" : "license-unverified-badge"}`}
          aria-label={isVerified ? "Verified" : "Not Verified"}
          aria-expanded={openTooltipFor === pillKey}
          onClick={(e) => {
            e.stopPropagation();
            setOpenTooltipFor((prev) => (prev === pillKey ? null : pillKey));
          }}
        >
          <span className="vettify-pill__icon small-dot">
            <img src={clearBadgeMarkWhite} alt="" />
          </span>
          <span className="vettify-pill__text">{isVerified ? "Verified" : "Not Verified"}</span>
        </button>
        <span role="tooltip" className={`info-tooltip ${openTooltipFor === pillKey ? "open" : ""}`}>
          {isVerified ? VERIFICATION_TOOLTIP_COPY : UNVERIFIED_TOOLTIP_COPY}
        </span>
      </span>
    );
  };

  useEffect(() => {
    const load = async () => {
      if (!id || isTempPlaceholderId(id)) {
        setRecord(null);
        setError("This claim link isn't available yet. Please sign up to claim and verify.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_BASE}/api/placeholder/${id}`);
        if (!res.ok) {
          const text = await res.text();
          let message = text;
          try {
            const parsed = JSON.parse(text);
            message = parsed?.error || text;
          } catch (_err) {
            // ignore
          }

          if (res.status === 404) {
            setError("This claim link couldn't be found. The profile may not be created yet.");
          } else {
            setError(message || "Unable to load profile.");
          }
          setRecord(null);
          return;
        }
        setRecord(await res.json());
      } catch (err) {
        setError(err?.message || "Unable to load profile.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const displayName = useMemo(() => {
    const first = record?.firstName || "";
    const last = record?.lastName || "";
    return `${first} ${last}`.trim() || "Unclaimed profile";
  }, [record]);

  const headlineName = useMemo(() => {
    const first = record?.firstName || "";
    const last = record?.lastName || "";
    return (first || last) ? displayName : "Name unavailable";
  }, [record, displayName]);

  return (
    <LegalLayout
      title="Claim this Profile"
      intro="Claiming starts identity + license verification and lets you manage what appears publicly."
      eyebrow="Claim"
    >
      {error && <p className="error-message">{error}</p>}
      {loading && <p className="muted">Loading...</p>}

      {record && (
        <>
          <div className="person-card unverified-card claim-preview-card">
            <div className="person-header">
              <div>
                <h4>{headlineName}</h4>
              </div>
              {renderVerificationPill(false, "claim-preview")}
            </div>

            <div className="person-meta">
              {(record.phoneDisplay || record.phone) && (
                <span>
                  <PhoneIcon className="card-line-icon" />
                  {record.phoneDisplay || record.phone}
                </span>
              )}
              {record.state && (
                <span>
                  <LicenseIcon className="card-line-icon" />
                  {record.state}
                </span>
              )}
              {(record.emailDisplay || record.email) && (
                <span>
                  <EmailIcon className="card-line-icon" />
                  {record.emailDisplay || record.email}
                </span>
              )}
              {record.company && (
                <span>
                  <CompanyIcon className="card-line-icon" />
                  {record.company}
                </span>
              )}
              {record.industry && (
                <span>
                  <CompanyIcon className="card-line-icon" />
                  {record.industry}
                </span>
              )}
            </div>

            <p className="unverified-disclaimer">{CLAIM_PAGE_UNCLAIMED_COPY}</p>
          </div>

          <p className="unverified-disclaimer verification-disclaimer" role="note" aria-label="Verification disclaimer">
            {VERIFICATION_TOOLTIP_COPY}
          </p>

          <div className="unverified-actions" style={{ marginTop: 12 }}>
            <Link to={`/login?mode=signup&claim=${encodeURIComponent(id)}`} className="primary claim-cta">
              Claim & Verify
            </Link>
          </div>
        </>
      )}

      {!loading && !record && (
        <div className="panel" style={{ marginBottom: 14 }}>
          <h3 style={{ marginTop: 0 }}>Claim & Verify</h3>
          <p className="unverified-disclaimer" style={{ marginTop: 8 }}>
            {UNVERIFIED_TOOLTIP_COPY}
          </p>
          <p className="unverified-disclaimer verification-disclaimer" role="note" aria-label="Verification disclaimer">
            {VERIFICATION_TOOLTIP_COPY}
          </p>
          <div className="unverified-actions" style={{ marginTop: 12 }}>
            <Link to="/login?mode=signup" className="primary claim-cta">
              Get Verified
            </Link>
            <Link to="/" className="ghost claim-cta">
              Search again
            </Link>
          </div>
        </div>
      )}
    </LegalLayout>
  );
}
